import { useCallback, useContext, useEffect, useState } from 'react';

import { getParamData } from 'component/Accommodation/Recommender/__helper/getParamData';
import {
    trackData,
    trackImpressionProductClick,
} from 'component/Accommodation/Recommender/__helper/trackData';
import { TrackingLabels } from 'component/Accommodation/Recommender/types';

import {
    IntersectionObserverOptions,
    useIntersectionObserver,
} from 'hooks/useIntersectionObserver';
import { useQueryEndpoint } from 'hooks/useQueryEndpoint';

import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import trackingEvent from 'utility/Track/trackingHelper';

import { HotelData } from 'types/legacy/backendInformation';
import { ApiEndpoint } from 'types/Request/endpoint';

import { ResponseStatus } from 'proto/Api/ResponseStatus';

import { trackProductClick, trackProductClickTop } from '../__helper/trackData';
import { PortalDefinition } from '../types';

const OBSERVER_OPTIONS: IntersectionObserverOptions = {
    rootMargin: '1px',
    triggerOnce: true,
};

export const useRecommender = () => {
    const { components, react } = useContext(CommunicationContext);
    const hotelData =
        components.backendInformationFunc.getData<{
            hotel: HotelData;
        }>('pageData')?.hotel;

    const { isFetching, error, data, refetch } = useQueryEndpoint(
        ApiEndpoint.HotelRecommendationList,
        {
            requestData: getParamData(hotelData?.id ?? 0),
        },
        {
            enabled: !!hotelData?.id,
            queryKey: [ApiEndpoint.HotelRecommendationList, hotelData?.id],
        }
    );
    const updateRecommender = useCallback(() => refetch(), [refetch]);

    useEffect(() => {
        react.bind('updateRecommender', updateRecommender);

        return () => {
            react.unbind('updateRecommender', updateRecommender);
        };
    });

    const callbackVisibleBottom = useCallback(() => {
        trackingEvent({
            category: TrackingLabels.BOTTOM,
            label: TrackingLabels.BOTTOM,
            action: 'user_visible',
        });
    }, []);

    const visibleBottomIntersectionObserver = useIntersectionObserver(
        OBSERVER_OPTIONS,
        callbackVisibleBottom
    );
    const callbackVisibleTop = useCallback(() => {
        trackingEvent({
            category: TrackingLabels.TOP,
            label: TrackingLabels.TOP,
            action: 'user_visible',
        });
    }, []);
    const visibleTopIntersectionObserver = useIntersectionObserver(
        OBSERVER_OPTIONS,
        callbackVisibleTop
    );
    //initially only bottom recommender is visible
    const [recommenderTargets, setRecommenderTargets] = useState<
        PortalDefinition[]
    >([
        {
            id: '#portal-recommender',
            trackMethod: trackImpressionProductClick,
            intersectionObserver: visibleBottomIntersectionObserver,
        },
    ]);

    const callback = useCallback(() => {
        setRecommenderTargets([
            {
                id: '#portal-recommender',
                trackMethod: trackProductClick,
                intersectionObserver: visibleBottomIntersectionObserver,
            },
            {
                id: '#portal-recommender-top',
                trackMethod: trackProductClickTop,

                intersectionObserver: visibleTopIntersectionObserver,
                position: 'top',
            },
        ]);
        !isFetching &&
            !error &&
            data?.recommendationList.length &&
            trackData(data?.recommendationList, TrackingLabels.TOP);
    }, [
        visibleBottomIntersectionObserver,
        visibleTopIntersectionObserver,
        isFetching,
        error,
        data?.recommendationList,
    ]);

    const intersectionObserver = useIntersectionObserver(
        OBSERVER_OPTIONS,
        callback
    );

    const onMoreClick = () => {
        components.travelsearch.offerlistDynamicService.loadStepHotelList();
    };

    useEffect(() => {
        data?.recommendationList.length &&
            trackData(data?.recommendationList, TrackingLabels.BOTTOM);

        intersectionObserver(
            document.getElementById('recommender-top-load-indicator')
        );
    }, [data?.recommendationList, intersectionObserver]);

    return {
        recommenderTargets,
        onMoreClick,
        isFetching,
        error,
        data,
        hotelItemList:
            !error &&
            data?.status !== ResponseStatus.Empty &&
            !!data?.recommendationList.length
                ? data?.recommendationList
                : [],
        hotelName: hotelData?.name ?? '',
    };
};
